import React, {FC} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import styles from './LeftPanel.module.scss'
import {
  ChatType,
  QuickFilterTab,
  SortOrder,
  ConversationData,
  TeamMember,
} from '../../types/inbox-v3.types'
import ConversationCard from '../Shared/ConversationCard'
import RmsConversationList from './RmsConversationList'

// RMS conversation type
interface RmsConversation {
  rmsThreadKey: string
  subscriberNumber: string
  message: string
  fields?: Array<Array<{name: string; value: string}>>
}

interface LeftPanelProps {
  chatType: ChatType
  onChatTypeChange: (type: ChatType) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  quickFilter: QuickFilterTab
  onQuickFilterChange: (filter: QuickFilterTab) => void
  sortOrder: SortOrder
  onSortOrderChange: () => void
  onFilterClick: () => void
  conversations: ConversationData[]
  selectedConversationId: string | null
  selectedConversations: string[]
  onConversationSelect: (id: string, selected: boolean) => void
  onConversationClick: (conversation: ConversationData) => void
  onArchive: (id: string) => void
  onFlag: (id: string) => void
  onAssign?: (id: string, assignee: TeamMember) => void
  onTag?: (id: string) => void
  onUnsubscribe?: (id: string) => void
  onSubscribe?: (id: string) => void
  teamMembers?: TeamMember[]
  isBulkActionsActive?: boolean
  openCount: number
  isLoading: boolean
  // RMS props
  selectedRmsId?: string | null
  onRmsSelect?: (conversation: RmsConversation) => void
  // Email props
  selectedEmailId?: string | null
  refetchEmailTrigger?: number
}

const LeftPanel: FC<LeftPanelProps> = ({
  chatType,
  onChatTypeChange,
  searchQuery,
  onSearchChange,
  quickFilter,
  onQuickFilterChange,
  sortOrder,
  onSortOrderChange,
  onFilterClick,
  conversations,
  selectedConversationId,
  selectedConversations,
  onConversationSelect,
  onConversationClick,
  onArchive,
  onFlag,
  onAssign,
  onTag,
  onUnsubscribe,
  onSubscribe,
  teamMembers = [],
  isBulkActionsActive = false,
  openCount,
  isLoading,
  // RMS props
  selectedRmsId,
  onRmsSelect,
  // Email props
  selectedEmailId,
  refetchEmailTrigger,
}) => {
  return (
    <div className={styles.leftPanelContainer}>
      {/* Chat Type Selector */}
      <div className={styles.chatTypeSelector}>
        <button
          className={`${styles.chatTypeButton} ${chatType === 'inbox' ? styles.active : ''}`}
          onClick={() => onChatTypeChange('inbox')}
        >
          Inbox Chat
        </button>
        <OverlayTrigger
          placement='bottom'
          delay={{show: 250, hide: 400}}
          overlay={
            <Tooltip id='tooltip-rms'>
              <div style={{textAlign: 'left'}}>
                <span>Use Record Management System (RMS) Chat to enable:</span>
                <ul className='mb-0 ps-3 mt-2'>
                  <li>Secure communication about sensitive records</li>
                  <li>Faster internal collaboration</li>
                  <li>Audit-trail-enabled chats for compliance</li>
                </ul>
              </div>
            </Tooltip>
          }
        >
          <button
            className={`${styles.chatTypeButton} ${chatType === 'rms' ? styles.active : ''}`}
            onClick={() => onChatTypeChange('rms')}
          >
            RMS Chat
          </button>
        </OverlayTrigger>
      </div>

      {/* Search Bar with Filter and Sort - Only for Inbox Chat */}
      {chatType === 'inbox' && (
        <div className={styles.searchContainer}>
          <div className={styles.searchWrapper}>
            <i className={`bi bi-search ${styles.searchIcon}`}></i>
            <input
              type='text'
              className={styles.searchInput}
              placeholder='Search conversations...'
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.clearSearchButton}
                onClick={() => onSearchChange('')}
                title='Clear search'
                type='button'
              >
                <i className='bi bi-x'></i>
              </button>
            )}
          </div>
          <div className={styles.filterSortButtons}>
            <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-filter'>Filter</Tooltip>}>
              <button className={styles.iconButton} onClick={onFilterClick}>
                <i className='bi bi-funnel'></i>
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip id='tooltip-sort'>
                  {sortOrder === 'descend' ? 'Sort: Newest First' : 'Sort: Oldest First'}
                </Tooltip>
              }
            >
              <button
                className={`${styles.iconButton} ${sortOrder === 'ascend' ? styles.active : ''}`}
                onClick={onSortOrderChange}
              >
                <i className={`bi ${sortOrder === 'descend' ? 'bi-sort-down' : 'bi-sort-up'}`}></i>
              </button>
            </OverlayTrigger>
          </div>
        </div>
      )}

      {/* Quick Tabs - Only for Inbox Chat */}
      {chatType === 'inbox' && (
        <div className={styles.quickTabs}>
          <button
            className={`${styles.tabButton} ${quickFilter === 'all' ? styles.active : ''}`}
            onClick={() => onQuickFilterChange('all')}
          >
            All
          </button>
          <button
            className={`${styles.tabButton} ${quickFilter === 'open' ? styles.active : ''}`}
            onClick={() => onQuickFilterChange('open')}
          >
            Open
            {openCount > 0 && <span className={styles.badge}>{openCount}</span>}
          </button>
          <button
            className={`${styles.tabButton} ${quickFilter === 'archived' ? styles.active : ''}`}
            onClick={() => onQuickFilterChange('archived')}
          >
            Archived
          </button>
        </div>
      )}

      {/* Conversation List - Conditional based on chatType */}
      <div className={styles.conversationList}>
        {chatType === 'inbox' && (
          <>
            {isLoading ? (
              // Loading skeletons
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={styles.skeletonItem}>
                    <div className={styles.skeletonAvatar}></div>
                    <div className={styles.skeletonContent}>
                      <div className={styles.skeletonLine}></div>
                      <div className={styles.skeletonLine}></div>
                    </div>
                  </div>
                ))}
              </>
            ) : conversations.length === 0 ? (
              // Empty state
              <div className={styles.emptyList}>
                <i className='bi bi-inbox'></i>
                <p>No conversations found</p>
              </div>
            ) : (
              // Conversation cards
              conversations.map((conversation) => (
                <ConversationCard
                  key={conversation._id}
                  conversation={conversation}
                  isSelected={selectedConversations.includes(conversation._id)}
                  isActive={selectedConversationId === conversation._id}
                  isBulkActionsActive={isBulkActionsActive}
                  teamMembers={teamMembers}
                  onSelect={onConversationSelect}
                  onClick={() => onConversationClick(conversation)}
                  onArchive={onArchive}
                  onFlag={onFlag}
                  onAssign={onAssign}
                  onTag={onTag}
                  onUnsubscribe={onUnsubscribe}
                  onSubscribe={onSubscribe}
                />
              ))
            )}
          </>
        )}

        {chatType === 'rms' && onRmsSelect && (
          <RmsConversationList selectedId={selectedRmsId || null} onSelect={onRmsSelect} />
        )}
      </div>
    </div>
  )
}

export default LeftPanel